import axios from 'axios';
import router from '../../router'

import Step01 from './Steps/Step01.js'
import Step02 from './Steps/Step02.js'
import Step03 from './Steps/Step03.js'
import Step04 from './Steps/Step04.js'
import RoutePaths from '../../constants/routePaths'

export default {
    namespaced: true,
    modules: {
        Step01,
        Step02,
        Step03,
        Step04
    },
    state: {
        settings: [],
        noticeTypes: [],
        localities: [],
        parcelLockers: [],
        postOffices: [],
        countries: [],
        currentStep: 1,
        validationErrors: null,
        keepAtPostOfficeInformation: {},
        redeliveryToLockerInformation: {},
        trackingNumbersEligibleForRedelivery: [],

        showSnackbar: false,
        snackbarTimeout: 3000,
        snackbarText: '',
        saving: false,
        busy: false,
        uploadProgress: null,

        charges: {},
        confirmation: {},
        requestReference: null,
        isRedirectingToPay: false,
        isRedirectingBeforeEdits: false,
        isSubmitting: false

    },
    getters: {
        currentStep: state => state.currentStep,
        overlay: state => state.overlay,
        currentRequest: (state) => {
            return {
                requestReference: state.requestReference,
                ...Step01.state,
                ...Step02.state,
                ...Step03.state
            };
        },
        settings: state => state.settings,
        noticeTypes: state => state.noticeTypes,
        localities: state => state.localities,
        parcelLockers: state => state.parcelLockers,
        postOffices: state => state.postOffices,
        countries: state => state.countries,
        showSnackbar: state => state.showSnackbar,
        snackbarTimeout: state => state.snackbarTimeout,
        snackbarText: state => state.snackbarText,
        saving: state => state.saving,
        busy: state => state.busy,
        uploadProgress: state => state.uploadProgress,
        charges: state => state.charges,
        confirmation: state => state.confirmation,
        requestReference: state => state.requestReference,
        isRedirectingToPay: state => state.isRedirectingToPay,
        isRedirectingBeforeEdits: state => state.isRedirectingBeforeEdits,
        isSubmitting: state => state.isSubmitting,
        validationErrors: state => state.validationErrors,
        keepAtPostOfficeInformation: state => state.keepAtPostOfficeInformation,
        redeliveryToLockerInformation: state => state.redeliveryToLockerInformation,
        trackingNumbersEligibleForRedelivery: state => state.trackingNumbersEligibleForRedelivery,
        routePaths: () => RoutePaths
    },
    actions: {
        setSaving(context, isSaving) {
            context.commit("setSaving", isSaving)
        },
        setBusy(context, isBusy) {
            context.commit("setBusy", isBusy)
        },
        setUploadProgress(context, uploadProgress) {
            context.commit("setUploadProgress", uploadProgress)
        },
        setIsRedirectingToPay(context, isRedirectingToPay) {
            context.commit("setIsRedirectingToPay", isRedirectingToPay)
        },
        setIsRedirectingBeforeEdits(context, isRedirectingBeforeEdits) {
            context.commit("setIsRedirectingBeforeEdits", isRedirectingBeforeEdits)
        },
        setIsSubmitting(context, isSubmitting) {
            context.commit("setIsSubmitting", isSubmitting)
        },
        setCurrentStep(context, currentStep) {
            context.commit('setCurrentStep', currentStep);
        },
        setCharges(context, charges) {
            context.commit('setCharges', charges);
        },
        setConfirmation(context, confirmation) {
            context.commit('setConfirmation', confirmation);
        },
        setRequestReference(context, requestReference) {
            context.commit('setReference', requestReference);
        },
        setSnackbarVisibility(context, isSnackbarVisible) {
            context.commit('setSnackbarVisibility', isSnackbarVisible);
        },
        setValidationErrors(context, validationErrors) {
            context.commit('setValidationErrors', validationErrors);
        },
        setKeepAtPostOfficeInformation(context, keepAtPostOfficeInformation) {
            context.commit('setKeepAtPostOfficeInformation', keepAtPostOfficeInformation);
        },
        setRedeliveryToLockerInformation(context, redeliveryToLockerInformation) {
            context.commit('setRedeliveryToLockerInformation', redeliveryToLockerInformation);
        },
        setTrackingNumbersEligibleForRedelivery(context, trackingNumbersEligibleForRedelivery) {
            context.commit('setTrackingNumbersEligibleForRedelivery', trackingNumbersEligibleForRedelivery);
        },
        async pageLoad(context) {
            await context.dispatch('getSettings');
            await context.dispatch('getNoticeTypes');
            await context.dispatch('getLocalities');
            await context.dispatch('getParcelLockers');
            await context.dispatch('getPostOffices');
            await context.dispatch('getCountries');

            var requestReference = router.currentRoute.query.ref;

            if (requestReference) {
                // get data of existing request, prefill form, and go to final summary step
                await context.dispatch("getRequest", requestReference);
            }
            else {
                context.dispatch('resetCurrent');
                context.dispatch('Step01/resetCurrent');
                context.dispatch('Step02/resetCurrent');
                context.dispatch('Step03/resetCurrent');
                context.dispatch('Step04/resetCurrent');
            }
        },
        async getSettings(context) {
            await axios.get('/Settings')
                .then((response) => {
                    context.commit("setSettings", response.data);
                }, (error) => {
                    context.commit('setSnackbarText', "An error has occurred.");
                    context.commit('setSnackbarVisibility', true);
                    console.log(error);
                });
        },
        async getNoticeTypes(context) {
            await axios.get('/NoticeTypes')
                .then((response) => {
                    context.commit("setNoticeTypes", response.data);
                }, (error) => {
                    context.commit('setSnackbarText', "An error has occurred.");
                    context.commit('setSnackbarVisibility', true);
                    console.log(error);
                });
        },
        async getLocalities(context) {

            //for developers: add the frontend url (e.g. http://localhost:8080) to the CORS origins setting
            //of your local address database api
            //e.g.     <add key="CorsOrigins" value="http://localhost,http://localhost:8080" />
            await axios.get(`${process.env.VUE_APP_ADDRESS_FINDER_API_BASE_URL}${process.env.VUE_APP_ADDRESS_FINDER_API_PATH_GETALLTOWNS}`)
                .then((response) => {
                    context.commit("setLocalities", response.data);
                }, (error) => {
                    context.commit('setSnackbarText', "An error has occurred retrieving the list of localities.");
                    context.commit('setSnackbarVisibility', true);
                    console.log(error);
                })
        },
        async getParcelLockers(context) {
            await axios.get('/ParcelLockers')
                .then((response) => {
                    context.commit("setParcelLockers", response.data);
                }, (error) => {
                    context.commit('setSnackbarText', "An error has occurred.");
                    context.commit('setSnackbarVisibility', true);
                    console.log(error);
                });
        },
        async getPostOffices(context) {
            await axios.get('/PostOffices')
                .then((response) => {
                    context.commit("setPostOffices", response.data.filter(item => {
                        return item.isRedeliveryLocation
                     }));
                }, (error) => {
                    context.commit('setSnackbarText', "An error has occurred.");
                    context.commit('setSnackbarVisibility', true);
                    console.log(error);
                });
        },
        async getCountries(context) {
            await axios.get('/Countries')
                .then((response) => {
                    context.commit("setCountries", response.data);
                }, (error) => {
                    context.commit('setSnackbarText', "An error has occurred.");
                    context.commit('setSnackbarVisibility', true);
                    console.log(error);
                });
        },
        startRedeliveryRequest(context) {
            context.commit('setCurrentStep', 1);
        },
        async validateTracked(context, data) {
            // Only applicable for parcels with a type of 'Tracked'
            var barcodesToValidate = [];

            if (data.barcodes) {
                const maxBarcodes = data.barcodes.length;

                for (var i = 0; i < maxBarcodes; i++) {
                    if (data.barcodes[i]) {
                        barcodesToValidate.push(data.barcodes[i]);
                    }
                }
            }

            context.commit("setSaving", true);
            context.commit("setBusy", true);

            await axios.post('/MissedDelivery/ValidateTracked', { "Barcodes": barcodesToValidate })
                .then((result) => {
                    const data = result.data;

                    let validationErrors = {};
                    var validationErrorsFound = false;

                    if (data.trackingNumberMustExistInTT && data.trackingNumbersNotFoundInTT && data.trackingNumbersNotFoundInTT.length > 0) {
                        validationErrorsFound = true;
                        for (const tn of data.trackingNumbersNotFoundInTT) {
                            validationErrors[tn] = `Tracking number ${tn} not found. Please check you entered this correctly, and contact support if needed.`;
                        }
                    }

                    if (data.trackingNumbersNotEligibleForRedelivery && data.trackingNumbersNotEligibleForRedelivery.length > 0) {
                        validationErrorsFound = true;
                        for (const tn of data.trackingNumbersNotEligibleForRedelivery) {
                            var validationError = tn.ineligibleReason ? tn.ineligibleReason : `Tracking number ${tn.trackingNumber} is not eligible for redelivery. Please contact support if you have any queries.`;
                            validationErrors[tn.trackingNumber] = validationErrors[tn.trackingNumber] ? (validationErrors[tn.trackingNumber] + " ") : "" + validationError;
                        }
                    }

                    if (validationErrorsFound) {
                        context.commit('setValidationErrors', validationErrors);
                    } else {
                        context.commit('setKeepAtPostOfficeInformation', data.keepAtPostOfficeInformation);
                        context.commit('setRedeliveryToLockerInformation', data.redeliveryToLockerInformation);
                        context.commit('setTrackingNumbersEligibleForRedelivery', data.trackingNumbersEligibleForRedelivery);
                        context.commit('setValidationErrors', null);
                        context.commit('setCurrentStep', 2);
                    }
                }, () => {
                    context.commit('setSnackbarText', "An error has occurred.");
                    context.commit('setSnackbarTimeout', 3000);
                    context.commit('setSnackbarVisibility', true);
                }).finally(function () {
                    context.commit("setSaving", false);
                    context.commit("setBusy", false);
                });
        },
        async validateUntracked(context, data) {

            context.commit("setSaving", true);
            context.commit("setBusy", true);

            await axios.post('/MissedDelivery/ValidateUntracked', { "date": data.date })
                .then((result) => {
                    const data = result.data;

                    context.commit('setKeepAtPostOfficeInformation', data.keepAtPostOfficeInformation);
                    context.commit('setRedeliveryToLockerInformation', data.redeliveryToLockerInformation);
                    context.commit('setTrackingNumbersEligibleForRedelivery', null);
                    context.commit('setValidationErrors', null);
                    context.commit('setCurrentStep', 2);
                }, () => {
                    context.commit('setSnackbarText', "An error has occurred.");
                    context.commit('setSnackbarTimeout', 3000);
                    context.commit('setSnackbarVisibility', true);
                }).finally(function () {
                    context.commit("setSaving", false);
                    context.commit("setBusy", false);
                });
        },

        verifyEasipikCustomerCode(context, customerCode) {
            context.commit("setSaving", true);
            context.commit("setBusy", true);

            return axios.get('/MissedDelivery/VerifyEasipikCustomerCode', { params: { customerCode: customerCode } })
                .then((result) => {
                    if (result.data.found) {
                        return { customerFound: true, defaultEasiPikLockerId: result.data.defaultEasiPikLockerId };
                    }
                    else {
                        context.commit('setSnackbarText', result.data.message);
                        context.commit('setSnackbarTimeout', 10000);
                        context.commit('setSnackbarVisibility', true);
                        return { customerFound: false };
                    }
                }, () => {
                    context.commit('setSnackbarText', "An error has occurred.");
                    context.commit('setSnackbarTimeout', 3000);
                    context.commit('setSnackbarVisibility', true);
                    return { customerFound: false };
                }).finally(function () {
                    context.commit("setSaving", false);
                    context.commit("setBusy", false);
                });
        },
        saveRequest(context) {
            context.commit("setSaving", true)
            context.commit("setBusy", true)
            const currentRequest = context.getters.currentRequest;

            var formData = new FormData();
            if (currentRequest.notice.file) {
                //if editing an existing request, there might not be any new file to upload
                formData.append('File', currentRequest.notice.file, currentRequest.notice.file.name);
            }

            formData.append('Data', JSON.stringify(currentRequest)); //TODO RH 144253 Is there a way to send the object directly?

            var config = {
                onUploadProgress: function (progressEvent) {
                    var percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    context.commit('setUploadProgress', percentCompleted);
                }
            };

            axios.post('/MissedDelivery/Request', formData, config)
                .then((result) => {
                    const data = result.data;

                    context.commit('setCharges', data);
                    context.commit('setRequestReference', data.requestReference);
                    context.commit('setCurrentStep', 4);
                }, (error) => {
                    context.commit('setSnackbarText', "An error has occurred.");
                    context.commit('setSnackbarTimeout', 3000);
                    context.commit('setSnackbarVisibility', true);
                    console.log(error);
                }).finally(function () {
                    context.commit("setSaving", false);
                    context.commit("setBusy", false);
                    context.commit('setUploadProgress', null);
                });
        },
        async getRequest(context, requestRef) {
            context.commit("setBusy", true)

            await axios.get('/MissedDelivery/Request', { params: { requestReference: requestRef } })
                .then((result) => {
                    const data = result.data;
                    if (data.statusCode == "Submitted") {
                        context.commit("setIsRedirectingBeforeEdits", true)
                        //cannot edit request, redirect to homepage
                        router.push('/');
                    }
                    else {
                        context.commit('setRequestReference', data.requestReference);
                        context.dispatch("Step01/setNotice", data.notice);
                        context.dispatch("Step02/setRecipient", data.recipientInformation);
                        context.dispatch("Step03/setDeliveryAddress", data.deliveryAddress);
                        context.commit('setCharges', data.trackedItemsChargesInformation);
                        context.commit('setCurrentStep', 3);
                    }
                }, (error) => {
                    context.commit('setSnackbarText', "Failed to load existing request data.");
                    context.commit('setSnackbarTimeout', 3000);
                    context.commit('setSnackbarVisibility', true);
                    console.log(error);
                }).finally(function () {
                    context.commit("setBusy", false);
                });
        },
        submit(context) {
            context.commit("setSaving", true)
            context.commit("setBusy", true)
            context.commit("setIsSubmitting", true)

            const requestReference = context.getters.requestReference;
            axios.put('/MissedDelivery/Submit', {
                RequestReference: requestReference
            })
                .then(() => {
                    router.push(`/confirmation?ref=${requestReference}`)
                }, (error) => {
                    context.commit('setSnackbarText', "An error has occurred.");
                    context.commit('setSnackbarTimeout', 3000);
                    context.commit('setSnackbarVisibility', true);
                    console.log(error);
                }).finally(function () {
                    context.commit("setSaving", false)
                    context.commit("setBusy", false)
                });
        },
        pay(context) {
            context.commit("setSaving", true)
            context.commit("setBusy", true)
            context.commit("setIsRedirectingToPay", true)
            const requestReference = context.getters.requestReference;
            axios.put('/MissedDelivery/Pay', {
                RequestReference: requestReference
            })
                .then((response) => {
                    //redirect user to payment page
                    window.location.replace(response.data)
                }, (error) => {
                    context.commit('setSnackbarText', "An error has occurred.");
                    context.commit('setSnackbarTimeout', 3000);
                    context.commit('setSnackbarVisibility', true);
                    console.log(error);
                })
        },
        getConfirmation(context, reference) {

            context.commit("setBusy", true)

            axios.get('/MissedDelivery/Confirmation', { params: { requestReference: reference } })
                .then((response) => {
                    const data = response.data;
                    context.commit('setConfirmation', data);
                }, (error) => {
                    context.commit('setConfirmation', null);
                    context.commit('setSnackbarText', "An error has occurred.");
                    context.commit('setSnackbarTimeout', 3000);
                    context.commit('setSnackbarVisibility', true);
                    console.log(error);
                }).finally(function () {
                    context.commit("setBusy", false);
                });

        },
        resetCurrent(context) {
            context.commit('resetCurrent');
        }
    },
    mutations: {
        setSnackbarText(state, payload) {
            state.snackbarText = payload;
        },
        setSnackbarVisibility(state, payload) {
            state.showSnackbar = payload;
        },
        setSnackbarTimeout(state, payload) {
            state.snackbarTimeout = payload;
        },
        setSettings(state, payload) {
            state.settings = payload;
        },
        setNoticeTypes(state, payload) {
            state.noticeTypes = payload;
        },
        setLocalities(state, payload) {
            state.localities = payload;
        },
        setParcelLockers(state, payload) {
            state.parcelLockers = payload;
        },
        setPostOffices(state, payload) {
            state.postOffices = payload;
        },
        setCountries(state, payload) {
            state.countries = payload;
        },
        startRedeliveryRequest(state, payload) {
            state = { ...state, currentStep: payload.currentStep };
            router.push(RoutePaths.MissedDelivery);
        },
        setCurrentStep(state, payload) {
            state.currentStep = payload;
        },
        setSaving(state, payload) {
            state.saving = payload;
        },
        setBusy(state, payload) {
            state.busy = payload;
        },
        setUploadProgress(state, payload) {
            state.uploadProgress = payload;
        },
        setIsRedirectingToPay(state, payload) {
            state.isRedirectingToPay = payload;
        },
        setIsRedirectingBeforeEdits(state, payload) {
            state.isRedirectingBeforeEdits = payload;
        },
        setIsSubmitting(state, payload) {
            state.isSubmitting = payload;
        },
        setCharges(state, payload) {
            state.charges = payload;
        },
        setConfirmation(state, payload) {
            state.confirmation = payload;
        },
        setRequestReference(state, requestReference) {
            state.requestReference = requestReference
        },
        setValidationErrors(state, validationErrors) {
            state.validationErrors = validationErrors
        },
        setKeepAtPostOfficeInformation(state, keepAtPostOfficeInformation) {
            state.keepAtPostOfficeInformation = keepAtPostOfficeInformation
        },
        setRedeliveryToLockerInformation(state, redeliveryToLockerInformation) {
            state.redeliveryToLockerInformation = redeliveryToLockerInformation
        },
        setTrackingNumbersEligibleForRedelivery(state, trackingNumbersEligibleForRedelivery) {
            state.trackingNumbersEligibleForRedelivery = trackingNumbersEligibleForRedelivery
        },
        resetSnackbar(state) {
            state.showSnackbar = false;
            state.snackbarText = '';
            state.snackbarTimeout = 3000;
        },
        resetCurrent(state) {
            state.charges = {};
            state.confirmation = {};
            state.requestReference = null;
            state.isRedirectingToPay = false;
            state.isRedirectingBeforeEdits = false;
            state.isSubmitting = false;
            state.saving = false;
            state.busy = false;
        }
    }
}